import React, { useEffect, useState, useMemo } from "react";
import api from "../../api";
import { CheckCircle2, XCircle, Users } from "lucide-react";

const COMPANY_OPTS = [
  { value: "all", label: "Tutte le società" },
  { value: "teleborsa", label: "STUDIO LEGALE VINCI" },
  { value: "sit", label: "NOLIMITS srl" },
];

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      timeZone: "Europe/Rome",
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const StatBadge = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-3">
    <span className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}1a`, color }}><Icon size={22} /></span>
    <div>
      <div className="text-[22px] font-black text-[#16345c] leading-none">{value}</div>
      <div className="text-[12px] text-gray-500 mt-1">{label}</div>
    </div>
  </div>
);

export const TourAcksTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState("all");
  const [dept, setDept] = useState("all");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    setLoading(true);
    api.get("/reports/tour-acks").then((r) => setData(r.data)).catch((e) => console.error("Caricamento tour-acks non riuscito:", e)).finally(() => setLoading(false));
  }, []);

  const allItems = useMemo(() => data?.items || [], [data]);
  const depts = useMemo(() => Array.from(new Set(allItems.map((i) => i.dept).filter(Boolean))).sort(), [allItems]);

  const filtered = useMemo(() => allItems.filter((i) => {
    if (company !== "all" && i.company !== company) return false;
    if (dept !== "all" && i.dept !== dept) return false;
    if (status === "acked" && !i.acked) return false;
    if (status === "not_acked" && i.acked) return false;
    return true;
  }), [allItems, company, dept, status]);

  const fAcked = filtered.filter((i) => i.acked).length;
  const fTotal = filtered.length;

  const selectCls = "border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c] bg-white";

  return (
    <div data-testid="tour-acks-tab">
      <div className="grid grid-cols-3 gap-4 mb-5">
        <StatBadge icon={Users} label="Dipendenti totali" value={fTotal} color="#16345c" />
        <StatBadge icon={CheckCircle2} label="Hanno confermato" value={fAcked} color="#009640" />
        <StatBadge icon={XCircle} label="Non ancora confermato" value={fTotal - fAcked} color="#c0392b" />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select value={company} onChange={(e) => setCompany(e.target.value)} data-testid="tour-filter-company" className={selectCls}>
          {COMPANY_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={dept} onChange={(e) => setDept(e.target.value)} data-testid="tour-filter-dept" className={selectCls}>
          <option value="all">Tutti i reparti</option>
          {depts.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="tour-filter-status" className={selectCls}>
          <option value="all">Tutti gli stati</option>
          <option value="acked">Solo confermati</option>
          <option value="not_acked">Solo non confermati</option>
        </select>
      </div>

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400">Caricamento...</div>
      ) : fTotal === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400 text-[13px]">Nessun dipendente corrisponde ai filtri selezionati.</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="bg-[#16345c] text-white text-[12px] uppercase tracking-wide">
                <th className="text-left px-4 py-3 font-bold">Dipendente</th>
                <th className="text-left px-4 py-3 font-bold">Società</th>
                <th className="text-left px-4 py-3 font-bold">Stato</th>
                <th className="text-left px-4 py-3 font-bold">Data conferma</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} data-testid={`tour-ack-row-${it.id}`} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="text-[14px] font-bold text-[#16345c]">{it.name}{it.is_external ? <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 align-middle">EST</span> : null}</div>
                    <div className="text-[11px] text-gray-500">{it.matricola}{it.dept ? ` · ${it.dept}` : ""}</div>
                  </td>
                  <td className="px-4 py-3 text-[13px] text-gray-600">{it.company_label}</td>
                  <td className="px-4 py-3">
                    {it.acked ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700"><CheckCircle2 size={12} /> Confermato</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700"><XCircle size={12} /> Non confermato</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[13px] text-gray-600">{it.acked ? fmtDateTime(it.acked_at) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
